import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { inboxPath } from "../../utils/inboxPath";
import { GiHamburgerMenu } from "react-icons/gi";
import {
  AiOutlineClose,
  AiOutlineShoppingCart,
  AiOutlineSearch,
  AiOutlineUser,
  AiOutlineLogout,
  AiOutlineIdcard,
  AiOutlineAppstore,
  AiOutlineHeart,
} from "react-icons/ai";
import { useAuth } from "../../contexts/AuthContext";
import { useWishlist } from "../../contexts/WishlistContext";
import { API_BASE } from "../../api/api";
import { withViewTransition } from "../../utils/viewTransition";
import { hasConsent, getVisitorId } from "../../utils/consent";
import { trackNavImpression, trackNavClick } from "../../utils/analytics";
import { useViewability } from "../../utils/useViewability";
import { goToSearch } from "../../utils/search";
import "./Nav.css";
import Logo from "../shared/Logo";

// Emporia Brain v3.1, Phase 2.6 — dynamic navbar category ordering, built
// dark. MIDDLE_CATEGORY_LINKS is the ONLY thing GET /nav/categories is ever
// allowed to reorder (see backend/app/nav_personalization.py, which mirrors
// this exact list) — up to 2 of these pulled to the front, everything else
// keeping its current relative order. NEW_ARRIVALS_LINK is pinned
// separately, always rendered last among categories, exactly like today.
const MIDDLE_CATEGORY_LINKS = [
  { label: "Technology",     to: "/category/phones-tablets" },
  { label: "Fashion",        to: "/category/fashion-men" },
  { label: "Home & Living",  to: "/category/home-living" },
  { label: "Beauty",         to: "/category/beauty-health" },
  { label: "Kitchen & Appliances", to: "/category/appliances" },
  { label: "Drinks",         to: "/category/drinks" },
];
const NEW_ARRIVALS_LINK = { label: "New Arrivals", to: "/new-arrivals" };

const categorySlug = (to) => (to.startsWith("/category/") ? to.slice("/category/".length) : null);
const DEFAULT_MIDDLE_SLUGS = MIDDLE_CATEGORY_LINKS.map((l) => categorySlug(l.to));
const CATEGORY_SLUG_TO_LINK = Object.fromEntries(MIDDLE_CATEGORY_LINKS.map((l) => [categorySlug(l.to), l]));

// Client-side fail-open budget (Rule 1) — a slow/hanging personalization
// response must never delay or block the navbar; the default order is
// already in state, so a timeout just means "keep it."
const NAV_PERSONALIZATION_TIMEOUT_MS = 150;

// A navbar category chip is always in the fixed header, not something the
// shopper scrolls to reveal — a lower intersection threshold than
// ProductRail's is appropriate since the chip is either on-screen at
// mount or not, with no meaningful partial-visibility case to distinguish.
const CategoryNavLink = ({ to, label, position, className, style, onClick }) => {
  const ref = useRef(null);
  const slug = categorySlug(to);
  useViewability(ref, () => trackNavImpression(slug, position), { threshold: 0.5, minDurationMs: 1000 });
  return (
    <NavLink
      ref={ref}
      to={to}
      className={className}
      style={style}
      onClick={(e) => { trackNavClick(slug, position); onClick(e); }}
    >
      {label}
    </NavLink>
  );
};

// Cycles across genuinely different departments — a single static
// "Search suits, shirts, shoes…" placeholder reads as "that's all you
// sell," even though the catalog spans 20+ departments. Rotating examples
// demonstrate breadth instead of just asserting it.
const SEARCH_PLACEHOLDERS = [
  "Search suits, shirts, shoes…",
  "Search phones, laptops, electronics…",
  "Search watches, jewellery, bags…",
  "Search home & kitchen essentials…",
  "Search sports gear, outdoors…",
  "Search across 20+ departments…",
];
const PLACEHOLDER_INTERVAL_MS = 3200;

// Search suggestions: wait for a pause in typing, need 2+ characters, show 6.
export const SUGGEST_DELAY_MS = 300;
export const SUGGEST_MIN_CHARS = 2;
const SUGGEST_LIMIT = 6;

const Nav = ({ cartCount = 0, cartAnimKey = 0 }) => {
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [searchQ,     setSearchQ]     = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [liveActive,  setLiveActive]  = useState(false);
  const [showToast,   setShowToast]   = useState(false);
  // Emporia Brain v3.1, Phase 2.6 — always starts as the real default
  // order, exactly what a no-consent/dark-flag/error/timeout shopper
  // must see (Rule 12); only ever replaced by a validated response.
  const [middleSlugOrder, setMiddleSlugOrder] = useState(DEFAULT_MIDDLE_SLUGS);
  const { user, logout } = useAuth();
  // The badge next to "My Wishes". Guests keep a local list too, but the
  // menu it sits in only renders when signed in.
  const { count: wishCount = 0 } = useWishlist() ?? {};
  const navigate         = useNavigate();

  // Shared helper for this file's primary navigation links (logo, top
  // categories, cart) — normal left-click gets a transitioned navigation;
  // modified clicks (cmd/ctrl/shift/middle) fall through to the browser's
  // native "open in new tab" behavior untouched.
  const navWithTransition = (to) => (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    withViewTransition(() => navigate(to));
  };

  const dropdownRef      = useRef(null);
  const searchRef        = useRef(null);
  const cartWrapRef      = useRef(null);

  // Rotate the search placeholder across departments — invisible once the
  // shopper types anything (native placeholder behavior), so no need to
  // pause it on focus/value, just keep cycling underneath.
  useEffect(() => {
    const t = setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % SEARCH_PLACEHOLDERS.length);
    }, PLACEHOLDER_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const fn = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setAccountOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchFocused(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // Live product suggestions for the search bar, based on platform inventory.
  // Waits until typing pauses, needs 2+ characters, asks for only the 6 it
  // shows, and cancels the previous request when the text changes, so a slow
  // older answer can never replace a newer one (speed brief item 4).
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < SUGGEST_MIN_CHARS) { setSuggestions([]); return; }
    const controller = new AbortController();
    const t = setTimeout(() => {
      fetch(`${API_BASE}/products?q=${encodeURIComponent(q)}&limit=${SUGGEST_LIMIT}`, { signal: controller.signal })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => setSuggestions(data.slice(0, SUGGEST_LIMIT)))
        .catch(() => { if (!controller.signal.aborted) setSuggestions([]); });
    }, SUGGEST_DELAY_MS);
    return () => { clearTimeout(t); controller.abort(); };
  }, [searchQ]);

  // Poll for an active live shopping session
  useEffect(() => {
    let mounted = true;
    const check = () => {
      fetch(`${API_BASE}/live/sessions/active`)
        .then(r => r.json())
        .then(data => { if (mounted) setLiveActive(!!data.active); })
        .catch(() => {});
    };
    check();
    const interval = setInterval(check, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  // Emporia Brain v3.1, Phase 2.6 — dynamic navbar category ordering,
  // built dark. No "personalisation" consent means no request at all
  // (Rule 12) — the default order already in state stays exactly as is.
  // A 150ms AbortController timeout enforces the client-side fail-open
  // budget (Rule 1); any error, timeout, or a response that isn't a
  // genuine permutation of the known category slugs is silently ignored,
  // leaving the default order untouched either way.
  useEffect(() => {
    if (!hasConsent("personalisation")) return undefined;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), NAV_PERSONALIZATION_TIMEOUT_MS);
    fetch(`${API_BASE}/nav/categories?visitor_id=${encodeURIComponent(getVisitorId())}`, { signal: controller.signal })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(data => {
        const order = data && Array.isArray(data.order) ? data.order : null;
        const isValidPermutation = !!order
          && order.length === DEFAULT_MIDDLE_SLUGS.length
          && new Set(order).size === DEFAULT_MIDDLE_SLUGS.length
          && order.every(slug => DEFAULT_MIDDLE_SLUGS.includes(slug));
        if (isValidPermutation) setMiddleSlugOrder(order);
      })
      .catch(() => {})
      .finally(() => clearTimeout(timeoutId));
    return () => { clearTimeout(timeoutId); controller.abort(); };
  }, []);

  const orderedMiddleLinks = middleSlugOrder.map(slug => CATEGORY_SLUG_TO_LINK[slug]).filter(Boolean);
  const orderedCategoryLinks = [...orderedMiddleLinks, NEW_ARRIVALS_LINK];

  // Cart bump animation + toast on every add-to-cart
  useEffect(() => {
    if (!cartAnimKey) return;
    const el = cartWrapRef.current;
    if (!el) return;
    // Remove class first so the animation restarts even on rapid adds
    el.classList.remove("cart-bumped");
    void el.offsetWidth; // force sync reflow — restarts the keyframe
    el.classList.add("cart-bumped");
    setShowToast(true);
    const t = setTimeout(() => setShowToast(false), 2400);
    return () => clearTimeout(t);
  }, [cartAnimKey]);

  const handleLogout = () => { setAccountOpen(false); logout(); navigate("/"); };
  const closeAccount = () => setAccountOpen(false);

  const doSearch = (e) => {
    e.preventDefault();
    if (!goToSearch(navigate, searchQ)) return;
    setSearchQ("");
    setSuggestions([]);
    setSearchFocused(false);
    setMenuOpen(false);
  };

  const goToSuggestion = (product) => {
    setSearchQ("");
    setSuggestions([]);
    setSearchFocused(false);
    setMenuOpen(false);
    navigate(`/product/${product.id}`);
  };

  return (
    <header className="site-header">

      {/* ── Main nav ── */}
      <nav className="nav">
        {/* Hamburger */}
        <button className="nav-burger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          {menuOpen ? <AiOutlineClose /> : <GiHamburgerMenu />}
        </button>

        {/* Brand */}
        <Link to="/" className="nav-brand" onClick={navWithTransition("/")}>
          <Logo className="nav-logo" />
        </Link>

        {/* Desktop search */}
        <div className="nav-search-container" ref={searchRef}>
          <form className="nav-search-wrap" onSubmit={doSearch} role="search">
            <AiOutlineSearch className="nav-search-icon" />
            <input
              className="nav-search-input"
              placeholder={SEARCH_PLACEHOLDERS[placeholderIdx]}
              aria-label="Search"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              onFocus={() => setSearchFocused(true)}
            />
          </form>

          {searchFocused && searchQ.trim() && suggestions.length > 0 && (
            <div className="nav-search-suggestions">
              {suggestions.map(p => (
                <button
                  key={p.id}
                  type="button"
                  className="nav-suggestion-item"
                  onClick={() => goToSuggestion(p)}
                >
                  <span className="nav-suggestion-info">
                    <span className="nav-suggestion-name">{p.name}</span>
                    <span className="nav-suggestion-cat">{p.category}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="nav-actions">
          {/* Account dropdown */}
          <div className="account-menu" ref={dropdownRef}>
            <button
              type="button"
              className={`nav-icon account-trigger${accountOpen ? " active" : ""}${user ? " logged-in" : ""}`}
              onClick={() => setAccountOpen(p => !p)}
              aria-label="Account"
            >
              <AiOutlineUser />
              {user && <span className="account-dot" />}
            </button>

            {accountOpen && (
              <div className="account-dropdown">
                <div className="dropdown-header">
                  <span className="dropdown-label">{user ? "Signed in as" : "Welcome"}</span>
                  <span className="dropdown-email">{user ? user.email : "Sign in to your account"}</span>
                </div>
                <div className="dropdown-divider" />
                {user ? (
                  <>
                    {user.is_admin && (
                      <>
                        <Link to="/admin" className="dropdown-item admin-item" onClick={closeAccount}>
                          <AiOutlineAppstore className="dropdown-icon" />
                          Admin Panel
                        </Link>
                        <div className="dropdown-divider" />
                      </>
                    )}
                    {/* Orders and messages live in the inbox
                        (/account/inbox; order history is its Orders
                        folder), reached from My Account's inbox button. */}
                    <Link to="/account"    className="dropdown-item" onClick={closeAccount}><AiOutlineIdcard className="dropdown-icon" />My Account</Link>
                    <Link to="/wishes"     className="dropdown-item" onClick={closeAccount}>
                      <AiOutlineHeart className="dropdown-icon" />My Wishes
                      {wishCount > 0 && <span className="dropdown-count" aria-label={`${wishCount} wishes`}>{wishCount}</span>}
                    </Link>
                    <Link to="/deliveries" className="dropdown-item" onClick={closeAccount}><AiOutlineIdcard className="dropdown-icon" />Deliveries</Link>
                    <div className="dropdown-divider" />
                    <button type="button" className="dropdown-item danger" onClick={handleLogout}><AiOutlineLogout className="dropdown-icon" />Sign Out</button>
                  </>
                ) : (
                  <>
                    <Link to="/login"    className="dropdown-item" onClick={closeAccount}><AiOutlineUser    className="dropdown-icon" />Sign In</Link>
                    <Link to="/register" className="dropdown-item" onClick={closeAccount}><AiOutlineIdcard  className="dropdown-icon" />Create Account</Link>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Cart */}
          <div className="cart-icon-wrap" ref={cartWrapRef}>
            <Link to="/cart" className="nav-icon cart-icon" aria-label="Selections" onClick={navWithTransition("/cart")}>
              <AiOutlineShoppingCart />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </Link>
            {showToast && (
              <div className="cart-toast" role="status" aria-live="polite">
                ✓ Added to Selections
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── Category bar (desktop) ── */}
      <div className="cat-bar">
        <div className="cat-bar-inner">

          {/* All Categories link */}
          <Link to="/categories" className="cat-all-btn">All Categories</Link>

          <div className="cat-divider" />

          <div className="cat-links">
            {orderedMiddleLinks.map((l, i) => (
              <CategoryNavLink
                key={l.to}
                to={l.to}
                label={l.label}
                position={i}
                className={({ isActive }) => `cat-link${isActive ? " cat-active" : ""}`}
                onClick={navWithTransition(l.to)}
              />
            ))}
            <NavLink
              to={NEW_ARRIVALS_LINK.to}
              className={({ isActive }) => `cat-link${isActive ? " cat-active" : ""}`}
              onClick={navWithTransition(NEW_ARRIVALS_LINK.to)}
            >
              {NEW_ARRIVALS_LINK.label}
            </NavLink>
          </div>
          {liveActive && (
            <Link to="/live" className="cat-live-link">
              <span className="cat-live-dot" /> LIVE
            </Link>
          )}
          <Link to="/search?tag=Hot%20Deal" className="cat-deal-link">🔥 Flash Deals</Link>
        </div>
      </div>

      {/* ── Mobile slide-down menu ── */}
      <div className={`mobile-menu${menuOpen ? " open" : ""}`}>
        {/* Mobile search */}
        <form className="mobile-search-wrap" onSubmit={doSearch} role="search">
          <AiOutlineSearch className="nav-search-icon" />
          <input
            className="nav-search-input"
            placeholder={SEARCH_PLACEHOLDERS[placeholderIdx]}
            aria-label="Search"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
          />
        </form>
        {orderedCategoryLinks.map((l, i) => (
          <NavLink
            key={l.to}
            to={l.to}
            className={({ isActive }) => `mobile-link${isActive ? " active" : ""}`}
            style={{ "--i": i }}
            onClick={() => { trackNavClick(categorySlug(l.to), i); setMenuOpen(false); }}
          >
            {l.label}
          </NavLink>
        ))}
        {liveActive && (
          <Link to="/live" className="mobile-link mobile-live" style={{ "--i": orderedCategoryLinks.length }} onClick={() => setMenuOpen(false)}>
            <span className="cat-live-dot" /> Live Shopping
          </Link>
        )}
        <div className="mobile-divider" />
        {user ? (
          <>
            <Link to="/account"   className="mobile-link" style={{ "--i": 6 }} onClick={() => setMenuOpen(false)}>My Account</Link>
            <Link to="/wishes"    className="mobile-link" style={{ "--i": 7 }} onClick={() => setMenuOpen(false)}>
              My Wishes{wishCount > 0 ? ` (${wishCount})` : ""}
            </Link>
            {/* Kept here even though the dropdown no longer lists it: this
                menu is the only nav on a phone, and nothing reachable before
                may stop being reachable. */}
            <Link to={inboxPath()} className="mobile-link" style={{ "--i": 8 }} onClick={() => setMenuOpen(false)}>Inbox</Link>
            <button className="mobile-link mobile-logout" style={{ "--i": 9 }} onClick={() => { setMenuOpen(false); handleLogout(); }}>Sign Out</button>
          </>
        ) : (
          <>
            <Link to="/login"    className="mobile-link" style={{ "--i": 6 }} onClick={() => setMenuOpen(false)}>Sign In</Link>
            <Link to="/register" className="mobile-link" style={{ "--i": 7 }} onClick={() => setMenuOpen(false)}>Create Account</Link>
          </>
        )}
      </div>
    </header>
  );
};

export default Nav;
